/**
 * Local data ownership — one place that knows how to erase everything this app
 * has written to browser storage.
 *
 * Why a module and not two `removeItem` calls at the call sites: the keys
 * already span nine modules (`localTrips`, `JarvisTab` scan caches, `Dashboard`
 * settings, `preferences`, `client`, `onboardingTour`, `theme`, journal, flights)
 * and the list drifted out of date the moment it was written down. A sweep by
 * prefix stays correct when someone adds the tenth cache.
 *
 * The two owning call sites are `handleSignOut` (App.tsx) and the delete-account
 * flow (SettingsTab.tsx).
 */

import { clearPlanIntent } from '@/app/utils/planIntent';
import { clearExploreFavorites } from '@/app/data/exploreFavorites';
import { clearPreferences } from '@/app/api/preferences';
import { forgetLastKnownTrips } from './lastKnownTrips';

/**
 * Every key this app writes is prefixed `jarvis_`, `jarvis.` or `jt.`.
 *
 * Deliberately a pattern, not an enumeration. A hand-maintained list is exactly
 * how sign-out came to leave `jarvis_local_trips` and `jarvis_scans_<tripId>`
 * behind, and it would go stale again on the next feature.
 */
const OWNED_KEY = /^(jarvis[_.]|jt\.)/;

/**
 * Keys that survive a purge.
 *
 * Near-empty on purpose: a signed-out browser should be indistinguishable from
 * one that never signed in. Theme was the obvious candidate to keep — a device
 * preference, not user data — and was rejected, because "signing out leaves
 * nothing behind" is worth more than the sign-in screen remembering a colour.
 *
 * The one exception is the Add-to-Home-Screen counter. It is a device-scoped
 * install-nudge state (an integer and a boolean, no personal data), and it MUST
 * outlive sign-out: a user who signs out and back in must still be nudged on
 * their 1st/3rd/6th sign-in, which is impossible if the counter resets each
 * logout. It uses the `jt.` prefix (the storage-ownership convention) and earns
 * its place here rather than dodging the sweep with an off-namespace key
 * (web-app#536 review, M2). The per-session guard `jt.a2hs.session` is NOT
 * listed, so it still rides the purge and re-arms the count on the next sign-in.
 *
 * Anything added here needs a comment saying why it is not personal data, and
 * localDataPurge.test.ts must assert it survives.
 */
const PRESERVE = new Set<string>(['jt.a2hs.install.v1']);

// Exported for the test that asserts this set stays empty. Adding an entry
// should be a deliberate act with a visible diff and a failing test to
// justify, not a quiet one-line addition during an unrelated change — see
// localDataPurge.test.ts.
export const __preserveForTest = PRESERVE;

/**
 * Remove every key this app owns from both localStorage and sessionStorage, and
 * drop the service worker's cached API responses.
 *
 * Never throws. Storage and the Cache API can be disabled, quota-locked, or
 * blocked by privacy settings, and sign-out must not fail because a purge did —
 * a user who cannot sign out is worse off than one whose cache survived.
 *
 * `async` for the Cache API step only: the synchronous storage sweep below runs
 * to completion before the first `await`, so callers that do not await it (a
 * legacy event handler, the tests) still get storage cleared immediately.
 */
export async function purgeAllLocalUserData(): Promise<void> {
  for (const store of [localStorage, sessionStorage]) {
    try {
      // Snapshot the keys first: removing while iterating a live Storage
      // reindexes it and silently skips entries.
      const doomed = Object.keys(store).filter(
        (key) => OWNED_KEY.test(key) && !PRESERVE.has(key),
      );
      for (const key of doomed) {
        store.removeItem(key);
      }
    } catch (e) {
      // Storage unavailable (disabled, quota-locked, blocked by privacy
      // settings). Swallowed on purpose — sign-out must never fail because a
      // purge did — but visible in development, where a silent no-op here
      // would look exactly like a working purge.
      if (import.meta.env.DEV) {
        console.debug('[localDataPurge] storage unavailable:', e);
      }
    }
  }
  // A plan choice storage couldn't hold is kept in memory instead, and must
  // go with the rest.
  clearPlanIntent();
  // Same shape, same reason: Explore's saved experiences keep a live set beside
  // the stored row, and a purge that swept only storage would leave it answering
  // reads for the rest of this page session (#535 review).
  clearExploreFavorites();
  // Same in-memory-module-state shape: the preferences save echoes the record it
  // last READ as a full-record POST, so a cached record surviving sign-out would
  // let the next user's first save echo the previous user's unmanaged fields
  // (JAR-2267 review). Must not throw before the storage sweep — it can't; it's
  // a single assignment.
  clearPreferences();
  // And the trips the Dashboard last showed, which live only in memory (JAR-2266).
  forgetLastKnownTrips();
  // JAR-2935's budget-intelligence cache: mapped budget amounts keyed by trip id,
  // no user dimension — the fifth in-memory seam (JAR-2266/2267, #535 lineage).
  // Imported lazily: the data layer must not statically pull a React hooks
  // module (and its fetcher chain) into every importer of this one.
  const { purgeBudgetIntelligenceCache } = await import('@/app/hooks/useBudgetIntelligence');
  purgeBudgetIntelligenceCache();
  // JAR-2936's score-day cache: the same shape — explanation text keyed by trip
  // id with no user dimension — the sixth in-memory seam. Same lazy import.
  const { __resetServerScoreDayCache } = await import('@/app/hooks/useServerScoreDay');
  __resetServerScoreDayCache();
  // JAR-3708's photo caches: resolved destination photos, and the per-trip sights
  // backfill with its failure window — both keyed by trip/query with no user
  // dimension, so one user's sight names and photo URLs must not outlive their
  // session on a shared device (JAR-3708 review, MED 1). Same lazy import as above.
  const { __resetTripPhotoPlaces } = await import('@/app/hooks/useTripPhotoQuery');
  __resetTripPhotoPlaces();
  const { __resetDestinationPhotoCache } = await import('@/app/hooks/useDestinationPhoto');
  __resetDestinationPhotoCache();

  // The service worker's runtime cache holds authed 200 responses from the API
  // (NetworkFirst on api.jarvistravel.com, 24h TTL). Storage alone leaves them
  // readable offline by the next user on a shared, installed device, which
  // breaks this module's own "indistinguishable from never signed in" invariant
  // (web-app#536 review, M1). The precache is app shell, not personal — leave
  // it; drop only the API cache, keyed to the name set in vite.config.ts.
  //
  // Kept last: every synchronous clear above runs before this first `await`, so
  // a caller that does not await still gets storage and in-memory state cleared
  // immediately.
  try {
    if (typeof caches !== 'undefined') {
      await caches.delete('api-cache');
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.debug('[localDataPurge] API cache purge failed:', e);
    }
  }
}

/**
 * Legacy auth keys from before the Clerk migration.
 *
 * Clerk mints a token per request and never persists one, so nothing writes
 * these any more — but a browser that signed in during the JWT era still holds
 * the old value, and it survives indefinitely because only sign-out sweeps
 * storage and these users are already signed in (JAR-355, low). A bearer token
 * sitting in localStorage is exactly the thing an XSS reads.
 *
 * Called once at boot rather than folded into purgeAllLocalUserData: that runs
 * on the way *out*, and the point here is the session that never signs out.
 */
const LEGACY_AUTH_KEYS = ['jarvis_token', 'jarvis_refresh_token', 'jarvis_auth_token'];

export function purgeLegacyAuthKeys(): void {
  for (const store of [localStorage, sessionStorage]) {
    try {
      for (const key of LEGACY_AUTH_KEYS) store.removeItem(key);
    } catch {
      // Same reasoning as the sweep above — storage can throw (private mode,
      // quota, a disabled cookie jar) and booting the app must not depend on it.
    }
  }
}
